import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import OptionCheckbox from 'new-ui/TreatmentOptions/Checkbox';
import Button from 'new-ui/Components/Button';
import { THEMES } from 'new-ui/constants';
import { useMatchesFilter } from 'modules/matchesFilter/MatchesFilterContext';

import styles from './CategoryTypeSelector.module.css';

const INITIAL_CATEGORY_DISPLAY_LIMIT = 4;

const CategoryTypeSelector = ({
  categories = [],
  onApply = () => { console.warn('CategoryTypeSelector: onApply prop not provided.'); },
  onLearnMore = () => { console.warn('CategoryTypeSelector: onLearnMore prop not provided.'); },
  onCompleteAndAdvance = () => {},
}) => {
  const {
    interventionalTypes: selectedCategories,
    toggleInterventionalType,
  } = useMatchesFilter();

  const [showMore, setShowMore] = useState(false);

  const handleCheckboxChange = useCallback((value) => {
    toggleInterventionalType(value);
  }, [toggleInterventionalType]);

  const handleApplyClick = useCallback(() => {
    onApply(selectedCategories);
    onCompleteAndAdvance();
  }, [onApply, selectedCategories, onCompleteAndAdvance]);

  const handleLearnMoreClick = useCallback(() => {
    onLearnMore();
  }, [onLearnMore]);

  const handleShowMoreClick = () => {
    setShowMore(true);
  };

  const categoriesToShow = categories.length <= INITIAL_CATEGORY_DISPLAY_LIMIT + 1 || showMore
    ? categories
    : categories.slice(0, INITIAL_CATEGORY_DISPLAY_LIMIT);

  const shouldShowMoreButton = categories.length > INITIAL_CATEGORY_DISPLAY_LIMIT + 1 && !showMore;

  return (
    <div className={styles.categorySelectorContainer}>
      <p className={styles.selectorQuestion}>What categories of treatment you prefer?</p>

      <div className={styles.categoryList}>
        {categoriesToShow.map((category) => {
          if (!category || !category.value || !category.label) return null;
          return (
            <div
              key={category.value}
              className={styles.categoryItem}
              style={{ backgroundColor: category.color || '#f0f0f0' }}
            >
              <OptionCheckbox
                label={category.label}
                value={category.value}
                checked={selectedCategories.includes(category.value)}
                onChange={() => handleCheckboxChange(category.value)}
              />
            </div>
          );
        })}

        {shouldShowMoreButton && (
          <button
            type="button"
            onClick={handleShowMoreClick}
            className={styles.moreButton}
          >
            <span className={styles.plusIcon}>+</span>
            More
          </button>
        )}
      </div>

      <div className={styles.applyButtonContainer}>
        <Button
          title="Apply"
          action={handleApplyClick}
          theme={THEMES.ORANGE_FULL}
          className={styles.applyButton}
        />
      </div>

      <div className={styles.learnMoreContainer}>
        <button
          type="button"
          onClick={handleLearnMoreClick}
          className={styles.learnMoreLink}
        >
          Learn which categories are right for you
        </button>
      </div>
    </div>
  );
};

CategoryTypeSelector.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string,
    categories: PropTypes.array,
  })),
  onApply: PropTypes.func,
  onLearnMore: PropTypes.func,
  onCompleteAndAdvance: PropTypes.func,
};

export default CategoryTypeSelector;
